/**
 * `cloudConfig.ts` — environment-keyed cloud endpoint values.
 *
 * Mirrors Unity's `CloudSettings` (snake-cased JSON loaded from
 * `StreamingAssets/app-config.json`). Values are copied verbatim from
 * `AppConfiguration/Application/{dev,prod}/app-config.json` in this
 * repo. Keep them in sync with the Unity asset — the source of truth
 * for "which AWS account / table / bucket" is the Unity build artifact.
 *
 * Selection precedence (Vite):
 *   1. `VITE_CAMPFIRE_ENV` (`dev` | `prod`) — explicit override.
 *   2. `import.meta.env.PROD` — `vite build` default.
 *   3. fall through to `dev`.
 *
 * Notes:
 *   - `dataProxyEndpoint` is intentionally left blank for the web port:
 *     Unity's private data-proxy route uses a VPC endpoint
 *     (`vpce-...amazonaws.com`) which browsers can't reach.
 *     The Unity client falls through to `DynamoSdkclient` when this is
 *     empty (see `AwsMetadataStorageModel.cs:98`), and `HttpStorageModel`
 *     uses the same SDK path.
 */

export type CampfireEnv = "dev" | "prod";

export interface CloudConfig {
  readonly env: CampfireEnv;
  readonly region: string;
  readonly userPoolId: string;
  readonly userPoolClientId: string;
  readonly identityPoolId: string;
  readonly awsAccountId: string;
  /** Dynamo table holding project rows (`pk = "{customerId}:project"`). */
  readonly projectTableName: string;
  /** Dynamo table holding telemetry, workflow activity, and mutable workflow user-state rows. */
  readonly telemetryTableName: string;
  /** Dynamo table holding project-scoped AI question/answer transcripts. */
  readonly transcriptTableName: string;
  /** Bucket holding `account/{customerId}/projectdata/...` blobs (incl. thumbnails). */
  readonly projectBucket: string;
  readonly graphqlEndpoint: string;
  /**
   * Unity `deeplink_base_uri` — base for DESKTOP shared-project deep links,
   * used as
   * `{deeplinkBaseUri}?sl={linkId}&act={customerId}&prj={projectId}`
   * (`DeepLinkGenerator`). That host renders the website launcher, which
   * redirects into `c3d://query` and therefore cannot open the web client.
   * Kept for contract/reference parity; web-authored links are emitted on the
   * current web deployment by `SharedUsersModel.projectLinkUrl`.
   */
  readonly deeplinkBaseUri: string;
  /**
   * Unity `admin_site_base_uri`. App settings' "Account Settings" opens it with
   * `AdminSiteBaseUri.Replace("admin", "isoft?ctx=settings")` — Unity's own
   * spelling, a literal substring replace on the whole URI rather than a path
   * swap (`AppSettingsViewModel.OnAccountSettings`).
   */
  readonly adminSiteBaseUri: string;
  /**
   * Unity `support_site_base_uri`. The projects-grid Help button opens it
   * verbatim — `ProjectsViewModel.OnSupport()` is a bare
   * `Application.OpenURL(m_appConfig.SupportSiteBaseUri)` with no transform.
   *
   * Both deployed environments carry the SAME value, so unlike `deeplinkBaseUri`
   * there is no unverified-prod caveat here. Note the value committed in
   * `CampfireScenes/Assets/StreamingAssets/app-config.json` differs (a Notion
   * page) — that is the in-editor default, replaced at deploy by the
   * `AppConfiguration/Application/{dev,prod}` files, which are authoritative.
   */
  readonly supportSiteBaseUri: string;
  /** Unity `aws_sessions_url`; realtime session socket for project/user events. */
  readonly sessionsEndpoint: string;
  /**
   * `KnownEventTypes`-style sentinel used by `pk = "{customerId}:project"`
   * sk-range queries. Mirrors `MaxGuid` in `AWSDynamoHelpers.cs`.
   */
  readonly skEndSentinel: string;
  /**
   * Pixyz cloud-conversion (CAD import). Unity `CloudSettings.PiXYZStackName`
   * + the `NEW_PIXYZ` `-campfire-params` state machine. The web calls the
   * Step Functions state machine directly (no DataProxy), mirroring
   * `AwsPixyzImportProcessor.StartConversionJob`.
   */
  readonly pixyzStackName: string;
  /** Serialized active model-import profile → Step Functions `PARAMS`. */
  readonly pixyzParams: string;
}

const DEV: CloudConfig = {
  env: "dev",
  region: "us-east-2",
  userPoolId: "us-east-2_MmHAeNGQF",
  userPoolClientId: "4clm19alg2h4t5838iifvhp9u3",
  identityPoolId: "us-east-2:58108f13-a5ca-49fe-af7b-c21c5c8c229b",
  awsAccountId: "277720321324",
  projectTableName: "projectstore-metadata",
  telemetryTableName: "telemetry-events",
  transcriptTableName: "spatial-transcript",
  projectBucket: "campfire-277720321324-customerassets",
  graphqlEndpoint: "https://gqlapi-dev.campfire3d.com/graphql",
  deeplinkBaseUri: "https://app-dev.campfire3d.com/query",
  // Verbatim from `AppConfiguration/Application/dev/app-config.json:12`.
  adminSiteBaseUri: "https://app-dev.campfire3d.com/admin",
  // Verbatim from `AppConfiguration/Application/dev/app-config.json:13`.
  supportSiteBaseUri: "https://support.campfire3d.com",
  sessionsEndpoint: "wss://campssvc-dev.campfire3d.com/v1",
  skEndSentinel: "!project:ffffffff-ffff-ffff-ffff-ffffffffffff",
  pixyzStackName: "campfire-pixyz-processor-ec2",
  pixyzParams: '{"name":"profile_1","optimize_pct":80,"is_default_profile":true}',
};

const PROD: CloudConfig = {
  env: "prod",
  region: "us-east-2",
  userPoolId: "us-east-2_mP97DjfDm",
  userPoolClientId: "5hr1fvhg5serluo8kvn3pca0tj",
  identityPoolId: "us-east-2:d9107033-eb5f-4725-b980-01821b49e8f4",
  awsAccountId: "400933498319",
  projectTableName: "projectstore-metadata",
  telemetryTableName: "telemetry-events",
  transcriptTableName: "spatial-transcript",
  projectBucket: "campfire-400933498319-customerassets",
  graphqlEndpoint: "https://gqlapi.campfire3d.com/graphql",
  // Verbatim from `AppConfiguration/Application/prod/app-config.json:10`.
  deeplinkBaseUri: "https://app.campfire3d.com/query",
  // Verbatim from `AppConfiguration/Application/prod/app-config.json:11`.
  adminSiteBaseUri: "https://app.campfire3d.com/admin",
  // Verbatim from `AppConfiguration/Application/prod/app-config.json:12` —
  // identical to dev, which is correct, not a copy-paste slip.
  supportSiteBaseUri: "https://support.campfire3d.com",
  sessionsEndpoint: "wss://campssvc.campfire3d.com/v1",
  skEndSentinel: "!project:ffffffff-ffff-ffff-ffff-ffffffffffff",
  // Verbatim from `AppConfiguration/Application/prod/app-config.json:36`.
  pixyzStackName: "campfire-pixyz-processor-ec2",
  pixyzParams: '{"name":"profile_1","optimize_pct":80,"is_default_profile":true}',
};

/**
 * Step Functions ARN of the Pixyz "generic polygon" conversion state machine
 * (`NEW_PIXYZ` `-campfire-params` variant). Mirrors
 * `AwsPixyzImportProcessor.m_polygonStateMachineArn`.
 */
export function polygonStateMachineArn(config: CloudConfig): string {
  return `arn:aws:states:${config.region}:${config.awsAccountId}:stateMachine:${config.pixyzStackName}-statemachine-genericpolygon-campfire-params`;
}

export function loadCampfireEnv(): CampfireEnv {
  const explicit = (import.meta.env.VITE_CAMPFIRE_ENV as string | undefined)?.toLowerCase();
  if (explicit === "prod" || explicit === "dev") return explicit;
  return import.meta.env.PROD ? "prod" : "dev";
}

export function loadCloudConfig(): CloudConfig {
  return loadCampfireEnv() === "prod" ? PROD : DEV;
}
